// we need to do two things:
// - get the starting position of the modal, within the viewport
// - specify the starting width/height that should be as visible as possible on the given screen size

import { useEffect } from "react";

// we accept the modals specified "best" starting size, along with the absolute minimum sizes
// this will let us use window.innerHeight/Width to perform the calculations

// height and width can be "auto" but min/max cannot (they may also be suffixes by px (px needs to be included inthe result))

type WindowPositionElements = {
  height: string;
  width: string;

  minHeight?: string;
  minWidth?: string;

  dragOptions: {
    defaultPosition: {
      x: number;
      y: number;
    };
  };
};

type PositionType =
  | "center-top" // about me
  | "centered" // most alert/notifications
  | "bottom-right" // properties dialogs etc
  | "bottom-left" // run dialog
  | "normal"; // normal positioning (stack with start )

// for "normal" - we will start in the top left, and move 10px down and right each window.
// reset back to 0 every 5 windows
let windowIndex = 0;

export function getModalSizeProps(
  height: string,
  width: string,
  positionType: PositionType = "normal",
  minHeight?: string,
  minWidth?: string,
): WindowPositionElements {
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;
  const TASKBAR_HEIGHT = 28;
  const EDGE_MARGIN = 10;

  let parsedHeight = height === "auto" ? 400 : parseInt(height);
  let parsedWidth = parseInt(width);
  let defaultPosition = { x: 150, y: 50 };
  let adjustedHeight = height;
  let adjustedWidth = width;

  switch (positionType) {
    case "center-top":
      // centered horizontally, at top with 10px margin
      defaultPosition = {
        x: Math.max(EDGE_MARGIN, windowWidth / 2 - parsedWidth / 2),
        y: EDGE_MARGIN + 40,
      };
      // constrain width if it would go off-screen
      if (defaultPosition.x + parsedWidth > windowWidth - EDGE_MARGIN) {
        adjustedWidth = windowWidth - 2 * EDGE_MARGIN + "px";
      }
      break;

    case "centered":
      // centered both horizontally and vertically with 10px margin
      defaultPosition = {
        x: Math.max(EDGE_MARGIN, windowWidth / 2 - parsedWidth / 2),
        y: Math.max(EDGE_MARGIN, windowHeight / 2 - parsedHeight / 2),
      };
      // constrain dimensions if needed
      if (defaultPosition.x + parsedWidth > windowWidth - EDGE_MARGIN) {
        adjustedWidth = windowWidth - 2 * EDGE_MARGIN + "px";
      }
      if (defaultPosition.y + parsedHeight > windowHeight - EDGE_MARGIN) {
        adjustedHeight = windowHeight - 2 * EDGE_MARGIN + "px";
      }
      break;

    case "bottom-right":
      // positioned at bottom-right with 10px margin and taskbar clearance
      parsedHeight = height === "auto" ? 400 : parseInt(height);
      parsedWidth = parseInt(width);
      defaultPosition = {
        x: windowWidth - parsedWidth - EDGE_MARGIN,
        y: windowHeight - parsedHeight - TASKBAR_HEIGHT - EDGE_MARGIN,
      };
      // constrain if needed
      if (defaultPosition.x < EDGE_MARGIN) {
        adjustedWidth = windowWidth - 2 * EDGE_MARGIN + "px";
        defaultPosition.x = EDGE_MARGIN;
      }
      if (defaultPosition.y < EDGE_MARGIN) {
        adjustedHeight = windowHeight - TASKBAR_HEIGHT - 2 * EDGE_MARGIN + "px";
        defaultPosition.y = EDGE_MARGIN;
      }
      break;

    case "bottom-left":
      // positioned at bottom-left with 10px margin and taskbar clearance
      parsedHeight = height === "auto" ? 400 : parseInt(height);
      defaultPosition = {
        x: EDGE_MARGIN,
        y: windowHeight - parsedHeight - TASKBAR_HEIGHT - EDGE_MARGIN,
      };
      // constrain height if needed
      if (defaultPosition.y < EDGE_MARGIN) {
        adjustedHeight = windowHeight - TASKBAR_HEIGHT - 2 * EDGE_MARGIN + "px";
        defaultPosition.y = EDGE_MARGIN;
      }
      break;

    case "normal":
    default:
      // cascade positioning: start at 150, 50 and move 10px down/right each window, reset after 5 windows
      const offset = (windowIndex % 5) * 10;
      parsedHeight = height === "auto" ? 400 : parseInt(height);
      parsedWidth = parseInt(width);
      defaultPosition = {
        x: 150 + offset,
        y: 50 + offset,
      };
      // adjust if would go off-screen
      if (defaultPosition.x + parsedWidth > windowWidth - EDGE_MARGIN) {
        adjustedWidth = windowWidth - defaultPosition.x - EDGE_MARGIN + "px";
      }
      if (defaultPosition.y + parsedHeight > windowHeight - TASKBAR_HEIGHT - EDGE_MARGIN) {
        adjustedHeight =
          windowHeight - TASKBAR_HEIGHT - defaultPosition.y - EDGE_MARGIN + "px";
      }
      windowIndex++;
      break;
  }

  const returnValue: WindowPositionElements = {
    height: adjustedHeight,
    width: adjustedWidth,
    dragOptions: { defaultPosition },
  };

  // add optional min dimensions after allocation
  if (minHeight) returnValue.minHeight = minHeight;
  if (minWidth) returnValue.minWidth = minWidth;

  return returnValue;
}
